'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { earnSummaryBunja } from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import { currencyDetailsBunja } from '@flipster/r230522/original/currency-detail';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import { UsdCurrency, UsdPrecision, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import CumulativeEarnings from './cumulative-earnings';

export const OverviewSection = () => {
  const t = useTranslations();
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { currencyDetailsAtom, subscribeCurrencies } = useBunja(currencyDetailsBunja);

  const earnSummary = useAtomValue(earnSummaryAtom);
  const currencyDetail = useAtomValue(currencyDetailsAtom);

  const balanceUSDT = Num(earnSummary?.totalAppliedBalance ?? 0).toString();
  const balanceUSD =
    balanceUSDT && currencyDetail
      ? Num(balanceUSDT).dividedBy(currencyDetail.USD.usdtRate).toString()
      : Num(0).toString();
  const estimatedEarning = Num(earnSummary?.estimatedEarnings ?? 0).toString();

  const tooltipContentsForEarningProcess = t('Earn.r251008.Earnings_are_paid_daily_assets_process_info');
  const tooltipContentsForAprAppliedBalance = (
    <ul className={css({ list: 'disc' })}>
      <li>
        <FormattedMessage id='Autogen_common.r251126.s_Balance_used_for_daily_earnings_calculation' />
      </li>
      <li>
        <FormattedMessage id='Autogen_common.r251126.s_Converted_to_USDT_at_current_market_prices' />
      </li>
      <li>
        <FormattedMessage id='Autogen_common.r251126.s_Includes_unrealized_PnL_updated_on_every_entrance_to_earn_page' />
      </li>
      <li>
        <FormattedMessage id='Autogen_common.r251126.s_Calculated_based_on_earn_limit_for_each_APR_category' />
      </li>
    </ul>
  );

  useEffect(() => {
    const unsubscribe = subscribeCurrencies([UsdCurrency]);
    return () => {
      unsubscribe();
    };
  }, [subscribeCurrencies]);

  return (
    <article
      className={css({
        p: 1,
        bgGradient: 'programs.promotion',
        rounded: 8,
      })}
    >
      <div
        className={flex({
          align: 'flex-start',
          justify: 'space-between',
          p: [16, 20],
          color: 'content.default.level-5',
        })}
      >
        <div className={flex({ direction: 'column', justify: 'center', align: 'flex-start', gap: 4 })}>
          <h2 className={css({ textStyle: 'Title/S', whiteSpace: 'pre-wrap' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Est_earnings' />
          </h2>
          <Tooltip
            placement='bottom-start'
            maxWidth={300}
            mobileAlert={() => {
              alert.show({
                title: <FormattedMessage id='Autogen_common.r251126.s_Earning_process' />,
                message: tooltipContentsForEarningProcess,
              });
            }}
          >
            <Tooltip.Trigger css={{ textStyle: 'Text/S/Underline', color: 'content.default.level-5' }}>
              <FormattedMessage id='Autogen_common.r251126.s_Earning_process' />
            </Tooltip.Trigger>
            <Tooltip.Content>{tooltipContentsForEarningProcess}</Tooltip.Content>
          </Tooltip>
        </div>
        <div className={flex({ direction: 'column', align: 'flex-end', gap: 8 })}>
          <Formatted.Number
            value={estimatedEarning}
            precision={UsdtPrecision}
            toFixed
            css={{ textStyle: 'Title/S', color: 'content.default.level-5' }}
            suffix={<span className={css({ textStyle: 'Text/M/Regular' })}>{UsdtCurrency}</span>}
          />
          <div className={css({ display: 'flex', gap: 4, whiteSpace: 'nowrap' })}>
            <div className={badgeStyle}>
              <FormattedMessage id='Autogen_common.r251126.s_Daily' />
            </div>
            <div className={badgeStyle}>
              <FormattedMessage id='Autogen_common.r251126.s_All_assets_in_USDT' />
            </div>
          </div>
        </div>
      </div>
      <div
        className={stack({
          gap: 12,
          px: { base: 16, tablet: 20 },
          pt: { base: 16, tablet: 24 },
          pb: { base: 12, tablet: 24 },
          bg: 'background.default.level-1',
          roundedBottom: 8,
        })}
      >
        <CumulativeEarnings />
        <div className={flex({ align: 'flex-start', justify: 'space-between' })}>
          <span
            className={hstack({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-5',
            })}
          >
            <FormattedMessage id='Common.r251008.Apr_applied_balance' />
            <Tooltip
              placement='bottom'
              maxWidth={500}
              crossAxisOffset={50}
              mobileAlert={() => {
                alert.show({
                  title: 'APR applied balance',
                  message: tooltipContentsForAprAppliedBalance,
                  confirmText: 'OK',
                });
              }}
            >
              <Tooltip.Trigger>
                <IconButton icon={<InformationCircleIcon />} size='small' hierarchy='quaternary' />
              </Tooltip.Trigger>
              <Tooltip.Content>{tooltipContentsForAprAppliedBalance}</Tooltip.Content>
            </Tooltip>
          </span>
          <p
            className={css({
              justifySelf: 'flex-end',
              textAlign: 'right',
            })}
          >
            <Formatted.Number
              value={balanceUSDT}
              precision={UsdtPrecision}
              toFixed
              css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
              suffix={<span className={css({ textStyle: 'Text/M/Regular' })}>{UsdtCurrency}</span>}
            />
            <br />
            <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
              ≈ <Formatted.Number value={balanceUSD} precision={UsdPrecision} toFixed prefix='$' />
            </span>
          </p>
        </div>
      </div>
    </article>
  );
};

const badgeStyle = css({
  textStyle: 'Text/XS/Regular',
  color: 'content.default.level-5',
  py: 2,
  px: 8,
  borderRadius: 10,
  border: '0.5px solid {colors.border.default.level-4}',
});
